export type MetaWhatsappOnboarding = "cloud_api" | "coexistence";

export interface MetaWhatsappSession {
  wabaId: string;
  businessId?: string;
  phoneNumberId?: string;
  onboarding: MetaWhatsappOnboarding;
}

export type EmbeddedSignupEvent =
  | { kind: "finish"; session: MetaWhatsappSession }
  | { kind: "cancel"; step: string }
  | { kind: "error"; message: string };

const numericId = /^\d{5,40}$/;

function id(value: unknown): string | undefined {
  return typeof value === "string" && numericId.test(value) ? value : undefined;
}

/**
 * Legge un messaggio `WA_EMBEDDED_SIGNUP` inviato dalla finestra Meta.
 * Restituisce null per i messaggi che non riguardano l'iscrizione.
 *
 * - `FINISH` (numero nuovo / Cloud API): arrivano waba_id, business_id e phone_number_id.
 * - `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` (coesistenza con l'app WhatsApp Business):
 *   Meta manda solo waba_id; numero e portfolio li ricava il server.
 */
export function parseEmbeddedSignupMessage(raw: unknown): EmbeddedSignupEvent | null {
  let message: unknown = raw;
  if (typeof raw === "string") {
    try { message = JSON.parse(raw); } catch { return null; }
  }
  if (!message || typeof message !== "object") return null;
  const { type, event, data } = message as { type?: unknown; event?: unknown; data?: Record<string, unknown> };
  if (type !== "WA_EMBEDDED_SIGNUP") return null;
  const payload = data && typeof data === "object" ? data : {};

  if (event === "CANCEL" || typeof payload.current_step === "string") {
    return { kind: "cancel", step: typeof payload.current_step === "string" ? payload.current_step : "" };
  }
  if (event === "ERROR") {
    const text = typeof payload.error_message === "string" ? payload.error_message : "";
    return { kind: "error", message: text ? `Meta: ${text.slice(0, 300)}` : "Meta ha segnalato un errore durante il collegamento" };
  }
  if (event === "FINISH_ONLY_WABA") {
    return { kind: "error", message: "L'account WhatsApp è stato creato ma nessun numero è stato collegato. Riprova scegliendo il numero." };
  }

  const wabaId = id(payload.waba_id);
  if (event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
    if (!wabaId) return { kind: "error", message: "Meta non ha restituito l'account WhatsApp collegato" };
    return {
      kind: "finish",
      session: { wabaId, businessId: id(payload.business_id), phoneNumberId: id(payload.phone_number_id), onboarding: "coexistence" },
    };
  }

  // FINISH (o versioni precedenti senza campo event): servono tutti e tre gli ID.
  const businessId = id(payload.business_id);
  const phoneNumberId = id(payload.phone_number_id);
  if (wabaId && businessId && phoneNumberId) {
    return { kind: "finish", session: { wabaId, businessId, phoneNumberId, onboarding: "cloud_api" } };
  }
  return null;
}
